import { Box, Button, ButtonProps as GrommetButtonProps } from 'grommet'
import { useMemo } from 'react'
import { palette } from '../../../themes/palette'

interface IBadgeProps extends Omit<GrommetButtonProps, 'icon'> {
  style?: React.CSSProperties
  onClick?: (React.MouseEventHandler<HTMLAnchorElement> & React.MouseEventHandler<HTMLButtonElement>) | undefined
  status?:
    | 'active'
    | 'on_leave'
    | 'on_notice_period'
    | 'retired'
    | 'dismissed'
    | 'resigned'
    | 'redundant'
    | 'upcoming'
    | 'checked_in'
    | string
  dot?: boolean
  icon?: JSX.Element
  text?: string
  bg?: string
}

export const Badge = ({ onClick, status, dot = false, icon, text, bg, ...props }: IBadgeProps) => {
  const color = useMemo(() => {
    const lowerCaseStatus = status?.toLowerCase()
    switch (lowerCaseStatus) {
      case 'active':
      case 'assigned':
      case 'completed':
      case 'in_stock':
      case 'normal':
      case 'available':
      case 'living':
      case 'administered':
      case 'dispensed':
      case 'verified':
      case 'approved':
      case 'paid':
      case 'user':
      case 'published':
      case 'improved':
        return { bg: '#EFFEE6', text: '#40B300' }
      case 'on_leave':
      case 'checked_in':
      case 'inactive':
      case 'pending':
      case 'pending_review':
      case 'partially_approved':
      case 'waiting':
      case 'draft':
      case 'low_stock':
      case 'expiring':
        return { bg: '#FFF8E5', text: '#FFB800' }
      case 'out_of_stock':
        return { bg: '#F3F4F6', text: '#6B7280' }
      case 'retired':
        return { bg: palette.sedonaSage, text: palette.squant }
      case 'resigned':
      case 'upcoming':
      case 'wasted':
      case 'scheduled':
        return { bg: palette.sapphire, text: palette.guardBlue }
      case 'ongoing':
      case 'in_consultation':
      case 'in_progress':
      case 'fulfilled':
        return { bg: '#E8F1FD', text: '#2F80ED' }
      case 'onhold':
      case 'missed':
      case 'not_in_service':
      case 'deceased':
      case 'review_requested':
      case 'abnormal':
      case 'rejected':
      case 'expired':
      case 'declined':
      case 'cancelled':
      case 'late_administered':
      case 'worse':
        return { bg: '#FEE6E6', text: '#FD1E1E' }
      case 'dismissed':
        return { bg: palette.carouselPink, text: palette.cranberrySplash }
      case 'on_notice_period':
        return { bg: palette.royalty, text: palette.chinesePurple }
      case 'signed':
      case 'verified':
      case 'resolved':
        return { bg: palette.lavenderMist, text: palette.violet }
      default:
        return {
          bg: '#eee',
          text: '#66666'
        }
    }
  }, [status])

  return (
    <Button
      color={color?.bg}
      gap={dot ? 'xsmall' : '0px'}
      icon={dot ? <Box background={color.text} height={'4px'} round="full" width={'4px'} /> : icon}
      pad={{ horizontal: '16px', vertical: '5px' }}
      size="small"
      {...props}
      onClick={onClick}
      style={{
        background: color.bg,
        borderRadius: '100px',
        color: color?.text,
        fontSize: '12px',
        fontWeight: 'bold',
        lineHeight: '16px',
        minWidth: '79px',
        textTransform: 'capitalize',
        textWrap: 'nowrap',
        ...props.style
      }}
    />
  )
}
